import type { Meta } from '@cubejs-client/core'
import { ArrowDown, ArrowUp, ChevronDown, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { messages } from '@/lib/messages'

import { listMembers, listViews, type MemberKind, type PickerOption } from './members'

const t = messages.memberPicker

/** Aranabilir seçenek listesi (popover + cmdk). `onPick` her seçimde; tekli kullanımda kapanır. */
function OptionPopover({
  label,
  summary,
  options,
  selected,
  closeOnPick,
  onPick,
}: {
  label: string
  summary: string
  options: PickerOption[]
  selected: string[]
  closeOnPick: boolean
  onPick: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={<Button variant="outline" size="sm" className="justify-between font-normal" />}
      >
        <span className="truncate">
          {label}: {summary}
        </span>
        <ChevronDown className="opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput placeholder={t.searchPlaceholder} aria-label={label} />
          <CommandList>
            <CommandEmpty>{t.empty}</CommandEmpty>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                // cmdk bu metinle arar: etiket ve kısa ad.
                value={`${option.label} ${option.value}`}
                data-checked={selected.includes(option.value)}
                onSelect={() => {
                  onPick(option.value)
                  if (closeOnPick) setOpen(false)
                }}
              >
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** Seçilebilir view'lar `meta`'dan gelir; yetkisiz view meta'da yoktur. Değer: view adı. */
export function ViewPicker({
  meta,
  value,
  onChange,
}: {
  meta: Meta
  value: string | undefined
  onChange: (view: string) => void
}) {
  const options = listViews(meta)
  const current = value === undefined ? undefined : options.find((o) => o.value === value)
  return (
    <OptionPopover
      label={t.view}
      summary={value === undefined ? t.none : (current?.label ?? t.notFound(value))}
      options={options}
      selected={value === undefined ? [] : [value]}
      closeOnPick
      onPick={onChange}
    />
  )
}

type MemberPickerProps = {
  meta: Meta
  view: string
  kind: MemberKind
  /** Alan etiketi; verilmezse türden (`Ölçü`, `Boyut`, `Zaman boyutu`). */
  label?: string
} & (
  | { multiple?: false; value: string | undefined; onChange: (member: string) => void }
  | { multiple: true; value: string[]; onChange: (members: string[]) => void }
)

/**
 * View'ın üyelerinden seçim; değer kısa ad. Çoklu seçimde sıra korunur (yukarı/aşağı).
 * Meta'da olmayan değer silinmez, "bulunamadı" olarak gösterilir.
 */
export function MemberPicker(props: MemberPickerProps) {
  const { meta, view, kind } = props
  const label = props.label ?? t[kind]
  const options = listMembers(meta, view, kind)
  const known = (value: string) => options.some((o) => o.value === value)
  const labelOf = (value: string) =>
    options.find((o) => o.value === value)?.label ?? t.notFound(value)

  if (!props.multiple) {
    return (
      <OptionPopover
        label={label}
        summary={props.value === undefined ? t.none : labelOf(props.value)}
        options={options}
        selected={props.value === undefined ? [] : [props.value]}
        closeOnPick
        onPick={props.onChange}
      />
    )
  }

  const { value, onChange } = props
  const move = (index: number, by: -1 | 1) => {
    const next = [...value]
    ;[next[index], next[index + by]] = [next[index + by], next[index]]
    onChange(next)
  }
  return (
    <div className="flex flex-col gap-1.5">
      <OptionPopover
        label={label}
        summary={t.add}
        options={options}
        selected={value}
        closeOnPick={false}
        onPick={(member) =>
          onChange(value.includes(member) ? value.filter((v) => v !== member) : [...value, member])
        }
      />
      {value.length > 0 && (
        <ol aria-label={t.selectedList(label)} className="flex flex-col gap-1">
          {value.map((member, index) => {
            const text = labelOf(member)
            return (
              <li
                key={member}
                className="border-input flex items-center gap-1 rounded-sm border px-2 py-1 text-sm"
              >
                <span
                  className={known(member) ? 'flex-1 truncate' : 'text-destructive flex-1 truncate'}
                >
                  {text}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  aria-label={t.moveUp(text)}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  aria-label={t.moveDown(text)}
                  disabled={index === value.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  aria-label={t.remove(text)}
                  onClick={() => onChange(value.filter((v) => v !== member))}
                >
                  <X />
                </Button>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
